import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { RESTAURANT_BACKEND_HOST, apiCall } from '../RestaurantBackend';
import { useAuth } from '../../../contexts/AuthContext';
import { getTenantServers, getTenantShifts, ALL_TENANTS, type TenantShiftRow } from '../../../services/scopedReadsService';
import FullPageSpinner from '../../FullPageSpinner';
import * as Sentry from '@sentry/react';
import './ServerManagement.css'; // Import the CSS for toggle styling
import { parseISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';

// Define server type
interface Server {
  id: string;
  name: string;
}

// Define shift type
interface Shift {
  id: string;
  server_id: string;
  server_name: string;
  started: string;
  has_notifications: boolean | number;
  notes: string | null;
}

const RecentShiftsTable: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const searchParams = new URLSearchParams(location.search);

  // Get pagination and filter parameters from URL
  const initialPage = parseInt(searchParams.get('page') || '1', 10);
  const initialServerId = searchParams.get('serverId') || '';

  const [servers, setServers] = useState<Server[]>([]);
  const [selectedServerId, setSelectedServerId] = useState<string>(initialServerId);
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingServers, setIsLoadingServers] = useState(true);
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [hasNextPage, setHasNextPage] = useState(false);
  const pageSize = 100;

  // Helper function to check if notifications are enabled
  const isNotificationsEnabled = (value: boolean | number): boolean => {
    return value === true || value === 1;
  };

  // Handler for server selection change
  const handleServerChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newServerId = e.target.value;
    setSelectedServerId(newServerId);

    // Update URL
    const params = new URLSearchParams(location.search);
    if (newServerId) {
      params.set('serverId', newServerId);
    } else {
      params.delete('serverId'); // Remove if "All Servers" is selected
    }
    params.set('page', '1'); // Reset to page 1 when filters change
    navigate(`${location.pathname}?${params.toString()}`);

    // Also update state
    setCurrentPage(1);
  };

  // Fetch servers data on mount. Demo/test servers are included on this page
  // so a URL-param serverId always has a matching <option> in the dropdown.
  useEffect(() => {
    const fetchServers = async () => {
      setIsLoadingServers(true);
      try {
        // feat-133 Batch-1: scoped ref-lookup replaces the unscoped
        // execute-select `SELECT id,name FROM server`. Manager/Corporate are
        // scoped to their own tenant server-side (the fix); Admin retains the
        // global listing via the `all` sentinel + ?all_tenants=true (PL D2
        // PRESERVE ruling, is_admin-gated). Demo/test servers stay included (no
        // exclude flag) so a URL-param serverId always has a matching <option>.
        const rawRole = user?.managementUser?.role;
        const role = rawRole
          ? rawRole.charAt(0).toUpperCase() + rawRole.slice(1).toLowerCase()
          : null;

        let results: { id: string; name: string }[] = [];
        if (role === 'Admin') {
          results = await getTenantServers(ALL_TENANTS, { allTenants: true });
        } else {
          const tenantId = user?.managementUser?.tenant_id;
          if (tenantId) {
            results = await getTenantServers(tenantId);
          }
        }
        // Add "All Servers" option; project to {id,name} (dropdown shape).
        setServers([
          { id: '', name: 'All Servers' },
          ...results.map((s) => ({ id: s.id, name: s.name })),
        ]);
      } catch (error) {
        console.error('Error fetching servers:', error);
        Sentry.captureException(error);
      } finally {
        setIsLoadingServers(false);
      }
    };
    fetchServers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.managementUser]);

  // Fetch shifts data
  useEffect(() => {
    const fetchShifts = async () => {
      setIsLoading(true);
      try {
        // feat-133 Batch-2: scoped shift-list replaces the unscoped execute-select
        // paginated `SELECT ... FROM shift`. Tenant is bound server-side from
        // UserContext (Manager/Corporate → own tenant, the security fix); Admin
        // retains the global listing via the `all` sentinel + ?all_tenants=true.
        // The endpoint returns the page_size+1 sentinel row (0-based `page`), so
        // the hasNextPage/slice(+1) logic below is unchanged.
        const rawRole = user?.managementUser?.role;
        const role = rawRole
          ? rawRole.charAt(0).toUpperCase() + rawRole.slice(1).toLowerCase()
          : null;
        const serverId =
          selectedServerId && selectedServerId !== '' ? selectedServerId : undefined;
        const page = currentPage - 1; // endpoint page is 0-based

        let fetchedShifts: TenantShiftRow[] = [];
        if (role === 'Admin') {
          fetchedShifts = await getTenantShifts(ALL_TENANTS, {
            allTenants: true,
            serverId,
            page,
            pageSize,
          });
        } else {
          const tenantId = user?.managementUser?.tenant_id;
          if (tenantId) {
            fetchedShifts = await getTenantShifts(tenantId, { serverId, page, pageSize });
          }
        }

        setHasNextPage(fetchedShifts.length > pageSize);
        setShifts(fetchedShifts.slice(0, pageSize) as Shift[]);
      } catch (error) {
        console.error('Error fetching shifts:', error);
        Sentry.captureException(error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchShifts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, selectedServerId, pageSize, user?.managementUser]);

  // Update URL when page changes via the URL
  useEffect(() => {
    const urlPage = parseInt(searchParams.get('page') || '1', 10);
    const urlServerId = searchParams.get('serverId') || '';

    if (urlPage !== currentPage) {
      setCurrentPage(urlPage);
    }
    if (urlServerId !== selectedServerId) {
      setSelectedServerId(urlServerId);
    }
  }, [location.search, searchParams]);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1) {
      // Update URL
      const params = new URLSearchParams(location.search);
      params.set('page', newPage.toString());
      navigate(`${location.pathname}?${params.toString()}`);

      // Also update state
      setCurrentPage(newPage);
      window.scrollTo(0, 0);
    }
  };

  const handleShiftClick = (shiftId: string, serverId: string) => {
    navigate(`/restaurant/shifts/${shiftId}/transcript/${serverId}`);
  };

  const handleToggleNotifications = async (shiftId: string, currentValue: boolean) => {
    try {
      const response = await apiCall(`/shifts/${shiftId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ has_notifications: !currentValue }),
      });

      if (!response.ok) {
        throw new Error('Failed to update notifications status');
      }

      // Update local state
      setShifts(shifts.map(shift =>
        shift.id === shiftId
          ? { ...shift, has_notifications: !currentValue }
          : shift
      ));

      console.log('Notifications status updated successfully');
    } catch (error) {
      console.error('Error updating notifications status:', error);
      Sentry.captureException(error);
      alert('Failed to update notifications status. Please try again.');
    }
  };

  const handleUpdateNotes = async (shiftId: string, notes: string) => {
    try {
      const response = await apiCall(`/shifts/${shiftId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ notes }),
      });

      if (!response.ok) {
        throw new Error('Failed to update notes');
      }

      // Update local state
      setShifts(shifts.map(shift =>
        shift.id === shiftId
          ? { ...shift, notes }
          : shift
      ));

      console.log('Notes updated successfully');
    } catch (error) {
      console.error('Error updating notes:', error);
      Sentry.captureException(error);
      alert('Failed to update notes. Please try again.');
    }
  };

  // Helper function to format date with timezone conversion
  const formatDate = (dateString: string): string => {
    try {
      // The date string from the database is now in UTC (after CONVERT_TZ in SQL)
      // Ensure it's treated as UTC by appending 'Z' if not present
      const utcDateString = dateString.endsWith('Z') ? dateString : `${dateString}Z`;
      const date = parseISO(utcDateString);
      
      // Format the date in Pacific time (America/Los_Angeles handles both PST and PDT automatically)
      // This will correctly account for daylight savings time
      return formatInTimeZone(date, 'America/Los_Angeles', 'MMM d, yyyy h:mm a');
    } catch (error) {
      console.error('Error formatting date:', error, dateString);
      return dateString;
    }
  };

  if (isLoading || isLoadingServers) { // Check both loading states
    return <FullPageSpinner />;
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Recent Server Shifts</h1>
        <div className="flex space-x-2">
          <Link
            to="/restaurant/servers"
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded"
          >
            Manage Servers
          </Link>
          <Link
            to="/restaurant"
            className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded"
          >
            Back to Dashboard
          </Link>
        </div>
      </div>
      <div className="mb-4 flex items-center">
        <div>
          <label htmlFor="serverFilter" className="mr-2 font-medium">
            Server:
          </label>
          <select
            id="serverFilter"
            value={selectedServerId}
            onChange={handleServerChange}
            className="px-3 py-1.5 border border-gray-300 rounded-md bg-white"
            disabled={isLoadingServers}
          >
            {isLoadingServers ? (
              <option>Loading servers...</option>
            ) : (
              servers.map(server => (
                <option key={server.id || 'all'} value={server.id}>
                  {server.name}
                </option>
              ))
            )}
          </select>
        </div>
      </div>

      <div className="bg-white shadow-md rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200 table-fixed">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/5">Server</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">Started</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">Notifications Were Sent</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Notes</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {shifts.map((shift) => (
              <tr key={shift.id} className="hover:bg-gray-50">
                <td
                  className="px-6 py-4 text-sm cursor-pointer"
                  onClick={() => handleShiftClick(shift.id, shift.server_id)}
                >
                  <div className="font-medium text-blue-600 hover:text-blue-800">{shift.server_name}</div>
                  <div className="text-xs text-gray-500 mt-1 truncate" title={shift.id}>{shift.id}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {formatDate(shift.started)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  {(() => {
                    const enabled = isNotificationsEnabled(shift.has_notifications);
                    return (
                      <span
                        className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border ${
                          enabled
                            ? 'bg-green-100 text-green-800 border-green-300'
                            : 'bg-red-100 text-red-800 border-red-300'
                        }`}
                      >
                        <span className={`mr-1.5 inline-block w-2 h-2 rounded-full ${enabled ? 'bg-green-600' : 'bg-red-600'}`}></span>
                        {enabled ? 'Yes' : 'No'}
                      </span>
                    );
                  })()}
                </td>
                <td className="px-6 py-4 text-sm text-gray-500">
                  <textarea
                    className="w-full border border-gray-300 rounded p-2 text-sm"
                    defaultValue={shift.notes || ''}
                    rows={2}
                    placeholder="Add notes..."
                    onBlur={(e) => {
                      const newValue = e.target.value;
                      if (newValue !== shift.notes) {
                        handleUpdateNotes(shift.id, newValue);
                      }
                    }}
                  />
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  <div className="flex space-x-2">
                    <Link
                      to={`/restaurant/shifts/${shift.id}/transcript/${shift.server_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-indigo-600 hover:text-indigo-900"
                    >
                      View Transcript
                    </Link>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex justify-between items-center">
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="px-4 py-2 bg-blue-500 text-white rounded disabled:bg-gray-300"
        >
          Previous
        </button>
        <span>Page {currentPage}</span>
        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={!hasNextPage}
          className="px-4 py-2 bg-blue-500 text-white rounded disabled:bg-gray-300"
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default RecentShiftsTable; 